import React, { useState, useEffect } from 'react';
import { FormControl, Button } from 'react-bootstrap';
import { isEmpty, omitBy, groupBy, get, map, isArray } from 'lodash';
import classnames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Filter from '../../components/Filters/Filter';
import ActiveFilters from '../../components/Filters/ActiveFilters';
import { LoadingSpinner } from '../../components/LoadingSpinner';

import './AudienceSegment.css';

const SegmentForm = ({
  name,
  filters,
  id,
  loading,
  handleSave,
  handleClose,
  backtoSegmentList,
  onUpdateFilterOptions,
  fieldState
}) => {
  const [segmentName, setSegmentName] = useState('');
  const [segmentFilters, setSegmentFilters] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    setSegmentName(name);
  }, [name]);

  useEffect(() => {
    setSegmentFilters(filters);
  }, [filters]);

  const handleChangeName = e => {
    setSegmentName(e.target.value);
  };

  const activeFiltersWithValues = () =>
    omitBy(segmentFilters, ({ value }) => isEmpty(value));

  const disabledSaveButton = () =>
    isEmpty(segmentName) || isEmpty(activeFiltersWithValues()) || loading;

  const previewButtonClassname = classnames(
    'tvt-audience-segment__button tvt-audience-segment__preview-button',
    {
      active: previewMode
    }
  );

  const handlePreviewMode = () => setPreviewMode(!previewMode);

  const handleChangeActiveFilter = filterIndex => () => {
    setActive(filterIndex);
    setPreviewMode(false);
  };

  const handleChange = (filterId, value) => {
    const filts = map(segmentFilters, filter =>
      filter.id === filterId ? { ...filter, value } : filter
    );

    // eslint-disable-next-line no-param-reassign
    if (fieldState !== undefined && filterId in fieldState) {
      fieldState[filterId].value = value;
    }

    setSegmentFilters(filts);
  };

  const countGroupSelected = g =>
    g.reduce((total, filter) => {
      if (filter.value && isArray(filter.value)) {
        return total + filter.value.length;
      }

      return filter.value ? total + 1 : total;
    }, 0);

  const getActiveFilters = filter =>
    omitBy({ [filter.id]: filter }, ({ value }) => isEmpty(value));

  const handleClearAll = () =>
    setSegmentFilters(
      map(segmentFilters, filter => ({
        ...filter,
        value: null
      }))
    );

  const handleClear = filterId => {
    const filts = map(segmentFilters, filter =>
      filter.id === filterId ? { ...filter, value: null } : filter
    );

    setSegmentFilters(filts);
  };

  const onClickSave = () => {
    handleSave(id, segmentName, segmentFilters);
  };

  const handleClearAllFromActive = filterId => () => handleClear(filterId);

  const filterGroups = groupBy(segmentFilters, f => f.section);
  const filterKeys = Object.keys(filterGroups);
  const filterItems = get(filterGroups, [filterKeys[active]], []);

  return (
    <>
      <div className="tvt-audience-segment__header">
        <FormControl
          type="text"
          className="tvt-audience-segment__name-input"
          placeholder="Segment Name"
          value={segmentName}
          onChange={handleChangeName}
        />
        <Button
          onClick={disabledSaveButton() ? () => null : onClickSave}
          className="tvt-audience-segment__button tvt-audience-segment__save-button"
          disabled={disabledSaveButton()}
        >
          Save {loading && <LoadingSpinner />}
        </Button>
        <Button onClick={handlePreviewMode} className={previewButtonClassname}>
          Preview
        </Button>
        <Button
          onClick={handleClose}
          className="tvt-audience-segment__button tvt-audience-segment__cancel-button"
        >
          Cancel
        </Button>
      </div>
      <div className="tvt-audience-segment__sidebar">
        {Object.keys(filterGroups).map((filter, index) => {
          const groupValueCount = countGroupSelected(filterGroups[filter]);
          const buttonClassname = classnames(
            'tvt-audience-segment__sidebar-button',
            {
              active: !previewMode && index === active,
              'has-filters-selected': groupValueCount > 0
            }
          );
          return (
            <button
              key={`audience-segment-button-${index}`}
              className={buttonClassname}
              onClick={handleChangeActiveFilter(index)}
            >
              {filter}
              {<span>{groupValueCount > 0 && groupValueCount}</span>}
            </button>
          );
        })}
        {backtoSegmentList && (
          <Button
            className="tvt-audience-segment__back-to-list"
            onClick={backtoSegmentList}
          >
            <FontAwesomeIcon icon="arrow-left" size="1x" />
            Back to segments
          </Button>
        )}
      </div>
      <div className="tvt-audience-segment__main">
        {!previewMode && (
          <>
            {filterItems.map(filter => {
              return (
                <div className="tvt-audience-segment__filter-selection-wrapper">
                  <div className="tvt-audience-segment__filter-selection">
                    <div className="tvt-audience-segment__title">
                      {filter.title}
                    </div>
                    <Filter
                      key={filter.id}
                      hasChanged={filter.hasChanged}
                      onChange={handleChange}
                      onUpdateFilterOptions={onUpdateFilterOptions}
                      {...filter}
                      hasBoolean={false}
                      fieldState={fieldState}
                      allowBulk={filter.allowBulk}
                    />
                  </div>
                  {!isEmpty(getActiveFilters(filter)) && (
                    <ActiveFilters
                      filters={getActiveFilters(filter)}
                      onClear={handleClear}
                      onClearAll={handleClearAllFromActive(filter.id)}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}
        {previewMode && (
          <>
            <div className="tvt-audience-segment__title">Preview</div>
            <div className="tvt-audience-segment__description">
              An overview of all selected filters.
              {isEmpty(activeFiltersWithValues()) && (
                <p>
                  <b>You should select at least 1 filter.</b>
                </p>
              )}
            </div>
            {!isEmpty(activeFiltersWithValues()) && (
              <ActiveFilters
                filters={activeFiltersWithValues()}
                onClear={handleClear}
                onClearAll={handleClearAll}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SegmentForm;
